class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartItems = document.querySelector('cart-drawer-items');
    document.addEventListener('recommendations::loaded', () => {
      this.cartItems.onChangeVariantRecommadation();
      this.checkClassProductRecommendationInCartUpsell();
    });
    this.setThemeEditorAccessibility();
  }

  setThemeEditorAccessibility() {
    if (!window.Shopify || !Shopify.designMode) return;

    const openWhenCartDrawerSelected = (event) => {
      if (event.detail?.sectionId !== 'cart-drawer') return;

      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        cartDrawer?.open();
      });
    };

    document.addEventListener('shopify:section:select', openWhenCartDrawerSelected);
    document.addEventListener('shopify:block:select', openWhenCartDrawerSelected);
  }

  checkClassProductRecommendationInCartUpsell() {
    let sectionHasProductRecommendation = false;
    const productRecommendations = this.querySelectorAll('product-recommendations-without-intersection-item');
    productRecommendations.forEach(elm => {
      if (elm.classList.contains('has-product-recommendation')) {
        sectionHasProductRecommendation = true;
      }
    });

    if (sectionHasProductRecommendation == false && productRecommendations[0]) {
      setTimeout(() => {
        productRecommendations[0].classList.add('has-product-recommendation');
      }, 100)
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    var scrollbarW = window.innerWidth - document.documentElement.clientWidth;

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      if (scrollbarW > 0) {
        document.documentElement.style.setProperty('--swift-scrollbar-compensation', scrollbarW + 'px');
        document.body.style.paddingRight = scrollbarW + 'px';
      }
      document.body.classList.add('overflow-hidden');
      this.classList.add('animate', 'active');
      document.body.classList.add('disable-scroll');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.syncUpsellCollapseState();
      },
      { once: true }
    );

    this.cartItems.onChangeVariantRecommadation();
  }

  syncUpsellCollapseState() {
    const upsell = this.querySelector('#CartDrawer-UpsellQuickSelect');
    if (!upsell) return;

    const cartBody = this.querySelector('.cart-drawer-body');
    if (!cartBody) return;

    const content = upsell.querySelector('[data-cart-quick-select-content]');
    const toggle = upsell.querySelector('[data-cart-quick-select-toggle]');
    if (!content || !toggle) return;

    const hasOverflow = cartBody.scrollHeight > cartBody.clientHeight;

    if (hasOverflow) {
      upsell.classList.remove('is-open');
      content.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      upsell.classList.add('is-open');
      content.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--swift-scrollbar-compensation');
    document.body.classList.remove('disable-scroll');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Pulse a line item once to confirm it was just added (mirrors the SWIFT
  // "just added" cue). The variant id is stashed on the drawer by product-form.js
  // before it re-renders. If the drawer was already open we pulse right away;
  // if it had to open first we wait for the open transition so the customer sees it.
  applyPendingPulse(wasAlreadyOpen) {
    const variantId = this._pulseVariantId;
    if (!variantId) return;
    this._pulseVariantId = null;

    const doPulse = () => {
      const wrapper =
        this.querySelector(`.cart-item-wrapper[data-item-id="${variantId}"]`) ||
        this.querySelector(`cart-remove-button [data-variant-id="${variantId}"]`)?.closest('.cart-item-wrapper') ||
        this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.closest('.cart-item-wrapper');
      if (!wrapper) return;

      wrapper.classList.remove('is-just-added');
      void wrapper.offsetWidth; // restart the animation if the class was still applied
      wrapper.classList.add('is-just-added');
      setTimeout(() => wrapper.classList.remove('is-just-added'), 500);
      wrapper.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    if (wasAlreadyOpen) {
      requestAnimationFrame(doPulse);
    } else {
      // Give the open transition (~0.35s) time to finish first.
      setTimeout(doPulse, 400);
    }
  }

  renderContents(parsedState, isOpenDrawer) {
    const wasAlreadyOpen = this.classList.contains('active');

    // When adding from within the already-open drawer (isOpenDrawer === 'false'),
    // keep the already-loaded upsell in place instead of letting the full re-render
    // blank it out and re-fetch — which the customer sees as the whole "You might
    // also like" section disappearing and reappearing. We hold a reference to the
    // live <product-recommendations> across the innerHTML swap and re-attach it, so
    // the carousel stays put (the added product is slid away client-side instead).
    let preservedUpsell = null;
    if (isOpenDrawer === 'false') {
      const liveUpsell = this.querySelector('product-recommendations');
      if (liveUpsell && liveUpsell.querySelector('cart-quick-select')) {
        preservedUpsell = liveUpsell;
      }
    }

    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this?.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (preservedUpsell) {
      const freshUpsell = this.querySelector('product-recommendations');
      if (freshUpsell) freshUpsell.replaceWith(preservedUpsell);
    }
    if (typeof updateHeaderCartCount === 'function') {
      updateHeaderCartCount(parsedState);
    }

    if (window.CartGwpGuard && !this._normalizingGwpCart) {
      this._normalizingGwpCart = true;
      window.CartGwpGuard.normalize()
        .then((changed) => {
          if (!changed) return;
          return window.CartGwpGuard.fetchCartDrawerSection().then((sectionHtml) => {
            this.renderContents({ sections: { 'cart-drawer': sectionHtml } }, isOpenDrawer);
          });
        })
        .catch((e) => {
          console.error('Error normalizing GWP cart state', e);
        })
        .finally(() => {
          this._normalizingGwpCart = false;
        });
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (isOpenDrawer != 'false') {
        this.open();
      }
      this.cartItems.onChangeVariantRecommadation();
      this.syncUpsellCollapseState();
      this.applyPendingPulse(wasAlreadyOpen);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
